import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { PropertyContext } from '../contexts/PropertyContext';
import { usePermissions } from '../hooks/usePermissions';
import { buildApiUrl } from '../config/environment';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { formatDate } from '../utils/dateUtils';
import PageWrapper from '../components/PageWrapper';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  BanknotesIcon,
  BuildingOfficeIcon,
  ArrowPathIcon,
  MagnifyingGlassPlusIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

// ─────────────────────────────────────────────────────────────
// Image Lightbox (zoom)
// ─────────────────────────────────────────────────────────────
const ImageLightbox = ({ src, alt, onClose }) => {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') setScale(s => Math.min(s + 0.25, 4));
      if (e.key === '-') setScale(s => Math.max(s - 0.25, 0.5));
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black bg-opacity-90 flex flex-col items-center justify-center"
      onClick={onClose}
    >
      {/* Controls */}
      <div className="absolute top-4 right-4 flex items-center gap-2" onClick={e => e.stopPropagation()}>
        <button
          onClick={() => setScale(s => Math.max(s - 0.25, 0.5))}
          className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-white text-sm font-bold"
          title="Zoom out (−)"
        >−</button>
        <span className="text-white text-sm font-medium px-2">{Math.round(scale * 100)}%</span>
        <button
          onClick={() => setScale(s => Math.min(s + 0.25, 4))}
          className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-white text-sm font-bold"
          title="Zoom in (+)"
        >+</button>
        <button
          onClick={onClose}
          className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-white ml-2"
          title="Close (Esc)"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div
        className="overflow-auto max-w-full max-h-full p-8"
        onClick={e => e.stopPropagation()}
        style={{ cursor: scale > 1 ? 'grab' : 'default' }}
      >
        <img
          src={src}
          alt={alt}
          style={{ transform: `scale(${scale})`, transformOrigin: 'top center', transition: 'transform 0.2s ease' }}
          className="max-w-full rounded shadow-2xl"
          draggable={false}
        />
      </div>
      <p className="absolute bottom-4 text-white text-xs opacity-60">Click outside or press Esc to close · +/− to zoom</p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Payment Detail Panel
// ─────────────────────────────────────────────────────────────
const PaymentDetailPanel = ({ paymentId, onVerified, agencyCurrency, user, agencyCountry }) => {
  const { hasPermission } = usePermissions();
  const currencySymbol = getCurrencySymbol(agencyCurrency);

  const [details, setDetails] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);

  const formatDateCtx = useCallback((dateString) => {
    return formatDate(dateString, { userPreference: user?.dateFormatPreference, agencyCountry });
  }, [user, agencyCountry]);

  // Fetch payment details + files whenever paymentId changes
  useEffect(() => {
    if (!paymentId) {
      setDetails(null);
      setFiles([]);
      setSelectedFile(null);
      return;
    }
    const token = sessionStorage.getItem('access_token');

    const fetchAll = async () => {
      setLoading(true);
      try {
        const [detailRes, filesRes] = await Promise.all([
          fetch(buildApiUrl(`/payments/${paymentId}`), {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(buildApiUrl(`/payments/${paymentId}/files`), {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const detailData = detailRes.ok ? await detailRes.json() : null;
        const filesData = filesRes.ok ? await filesRes.json() : [];
        const filesArr = Array.isArray(filesData) ? filesData : [];

        setDetails(detailData);
        setFiles(filesArr);
        setSelectedFile(filesArr.length > 0 ? filesArr[0] : null);
        setSelectedFileIndex(0);
      } catch (err) {
        console.error('Error loading payment detail:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [paymentId]);

  const handleVerify = async () => {
    if (!window.confirm('Verifying this payment will lock it from further editing. Do you wish to proceed?')) return;
    setVerifying(true);
    try {
      const token = sessionStorage.getItem('access_token');

      // POST to verify — empty body, user ID extracted from JWT server-side
      const res = await fetch(buildApiUrl(`/payments/${paymentId}/verify`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to verify payment');
      }

      // Refresh payment details via GET to get the updated verification fields
      const refreshRes = await fetch(buildApiUrl(`/payments/${paymentId}`), {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (refreshRes.ok) {
        const updated = await refreshRes.json();
        setDetails(updated);
      }

      alert('Payment verified successfully!');
      if (onVerified) onVerified(paymentId);
    } catch (err) {
      console.error('Error verifying payment:', err);
      alert(`Failed to verify payment: ${err.message}`);
    } finally {
      setVerifying(false);
    }
  };

  const selectFile = (file, index) => {
    setSelectedFile(file);
    setSelectedFileIndex(index);
  };

  const prevFile = () => {
    if (files.length === 0) return;
    const idx = (selectedFileIndex - 1 + files.length) % files.length;
    selectFile(files[idx], idx);
  };

  const nextFile = () => {
    if (files.length === 0) return;
    const idx = (selectedFileIndex + 1) % files.length;
    selectFile(files[idx], idx);
  };

  if (!paymentId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 py-20">
        <BanknotesIcon className="h-16 w-16 mb-4 opacity-30" />
        <p className="text-lg font-medium">Select a payment to review</p>
        <p className="text-sm mt-1">Choose from the list on the left</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500" />
      </div>
    );
  }

  if (!details) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-red-400 py-20">
        <ExclamationTriangleIcon className="h-12 w-12 mb-3" />
        <p className="text-sm">Failed to load payment details.</p>
      </div>
    );
  }

  const isVerified = (details.verification_status || details.VerificationStatus) === 'verified';
  const amountFormatted = details.amount?.formatted ||
    (details.amount?.amount != null
      ? `${currencySymbol}${(details.amount.amount / 100).toFixed(2)}`
      : '-');
  const verifiedAt = details.verified_at || details.VerifiedAt;
  const verifiedByUser = details.verified_by_user || details.VerifiedByUser;
  const transaction = details.account_transaction || details.AccountTransaction;

  const imageUrl = selectedFile
    ? (selectedFile.medium_url || selectedFile.thumbnail_url || selectedFile.original_url)
    : null;
  const isImage = imageUrl != null;

  return (
    <>
      {lightboxSrc && (
        <ImageLightbox
          src={lightboxSrc}
          alt="Payment document"
          onClose={() => setLightboxSrc(null)}
        />
      )}

      <div className="flex flex-col h-full overflow-y-auto">
        {/* Header bar */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-4 flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold">Payment #{details.id || details.ID}</h3>
              <p className="text-emerald-100 text-sm mt-0.5">{amountFormatted}</p>
            </div>

            {/* Status badge + Verify button stacked on the right */}
            <div className="flex flex-col items-end gap-2">
              {isVerified ? (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-white bg-opacity-20 rounded-full text-sm font-medium">
                  <CheckCircleSolid className="h-4 w-4 text-white" />
                  Verified
                </span>
              ) : (
                <>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-400 bg-opacity-30 rounded-full text-sm font-medium text-amber-100">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    Unverified
                  </span>

                  {/* Verify button — only shown to users with payment.verification permission */}
                  {hasPermission('payment.verification') && (
                    <button
                      onClick={handleVerify}
                      disabled={verifying}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-emerald-700 bg-white rounded-lg hover:bg-emerald-50 active:bg-emerald-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      {verifying ? (
                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircleIcon className="h-4 w-4" />
                      )}
                      {verifying ? 'Verifying…' : 'Verify Payment'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row flex-1 min-h-0">
          {/* Left: Payment info */}
          <div className="lg:w-1/2 p-5 space-y-4 border-r border-gray-200 overflow-y-auto">
            {/* Core fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Amount</p>
                <p className="text-xl font-bold text-gray-900">{amountFormatted}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Payment Date</p>
                <p className="text-sm font-medium text-gray-800">
                  {details.payment_date ? formatDateCtx(details.payment_date) : '-'}
                </p>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Reference</p>
              <p className="text-sm text-gray-800">{details.reference || details.Reference || <span className="text-gray-400">—</span>}</p>
            </div>

            {/* Linked Transaction */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Linked Transaction</p>
              {transaction ? (
                <div className="border border-green-200 rounded-lg p-3 bg-green-50">
                  <div className="flex items-center mb-2">
                    <CheckCircleSolid className="h-4 w-4 text-green-500 mr-1.5" />
                    <span className="text-sm font-semibold text-green-700">Transaction Linked</span>
                  </div>
                  <div className="text-xs text-gray-700 space-y-1 ml-5">
                    <p><span className="font-medium">ID:</span> {transaction.id || transaction.ID}</p>
                    <p><span className="font-medium">Date:</span> {formatDateCtx(transaction.date)}</p>
                    <p className="font-semibold text-green-600">
                      <span className="font-medium text-gray-700">Amount:</span>{' '}
                      {currencySymbol}{(transaction.amount / 100).toFixed(2)}
                    </p>
                    {transaction.description && (
                      <p><span className="font-medium">Description:</span> {transaction.description}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <p className="text-sm text-gray-400">No transaction linked</p>
                </div>
              )}
            </div>

            {/* Verification Status */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Verification Status</p>
              {isVerified ? (
                <div className="border border-green-200 rounded-lg p-3 bg-green-50">
                  <div className="flex items-center mb-2">
                    <CheckCircleSolid className="h-4 w-4 text-green-500 mr-1.5" />
                    <span className="text-sm font-semibold text-green-700">Verified</span>
                  </div>
                  <div className="text-xs text-gray-700 space-y-1 ml-5">
                    {verifiedAt && (
                      <p><span className="font-medium">Verified on:</span> {formatDateCtx(verifiedAt)}</p>
                    )}
                    {verifiedByUser && (
                      <p>
                        <span className="font-medium">Verified by:</span>{' '}
                        {verifiedByUser.given} {verifiedByUser.surname}
                        {verifiedByUser.email && (
                          <span className="text-gray-500"> ({verifiedByUser.email})</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="border border-amber-200 rounded-lg p-3 bg-amber-50">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 mr-1.5" />
                    <span className="text-sm font-semibold text-amber-700">Not Verified</span>
                  </div>
                  <p className="text-xs text-amber-600 mt-2">
                    This payment has not been verified yet. Verifying will lock it from further editing.
                    {hasPermission('payment.verification') && (
                      <span className="block mt-1 font-medium">Use the "Verify Payment" button above.</span>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right: Documents & Images */}
          <div className="lg:w-1/2 flex flex-col overflow-hidden">
            {/* File list header */}
            <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
              <p className="text-xs font-semibold text-gray-600">
                Documents {files.length > 0 && `(${files.length})`}
              </p>
              {files.length > 1 && (
                <div className="flex items-center gap-1">
                  <button onClick={prevFile} className="p-1 rounded hover:bg-gray-200 transition-colors">
                    <ChevronLeftIcon className="h-3.5 w-3.5 text-gray-500" />
                  </button>
                  <span className="text-xs text-gray-500">{selectedFileIndex + 1} / {files.length}</span>
                  <button onClick={nextFile} className="p-1 rounded hover:bg-gray-200 transition-colors">
                    <ChevronRightIcon className="h-3.5 w-3.5 text-gray-500" />
                  </button>
                </div>
              )}
            </div>

            {/* File thumbnails strip */}
            {files.length > 0 && (
              <div className="flex gap-2 px-4 py-2 overflow-x-auto border-b border-gray-200 bg-white flex-shrink-0">
                {files.map((f, idx) => {
                  const thumb = f.thumbnail_url || f.medium_url || f.original_url;
                  return (
                    <button
                      key={f.id}
                      onClick={() => selectFile(f, idx)}
                      className={`flex-shrink-0 w-12 h-12 rounded border-2 overflow-hidden transition-all ${
                        selectedFileIndex === idx ? 'border-emerald-500 shadow-sm' : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      {thumb ? (
                        <img src={thumb} alt={f.description || 'doc'} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Main preview area */}
            <div className="flex-1 overflow-y-auto p-4">
              {files.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 py-10">
                  <svg className="w-12 h-12 mb-2 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm">No documents attached</p>
                </div>
              ) : selectedFile ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-gray-500 truncate">{selectedFile.description || 'Document'}</p>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded capitalize">{selectedFile.file_type}</span>
                  </div>
                  {isImage ? (
                    <div
                      className="relative group cursor-zoom-in rounded-lg border border-gray-200 overflow-hidden bg-gray-50"
                      onClick={() => setLightboxSrc(imageUrl)}
                    >
                      <img
                        src={imageUrl}
                        alt={selectedFile.description || 'Payment document'}
                        className="w-full object-contain max-h-96 block"
                      />
                      {/* Zoom badge — sits in corner, no dark overlay that could obscure the image */}
                      <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-1.5 shadow-md pointer-events-none">
                        <MagnifyingGlassPlusIcon className="h-4 w-4 text-gray-600" />
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-lg border border-gray-200">
                      <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm text-gray-500 mb-3">{selectedFile.description || 'Document'}</p>
                      {selectedFile.original_url && (
                        <a
                          href={selectedFile.original_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
                        >
                          Open File
                        </a>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-400 text-right">
                    Uploaded {formatDateCtx(selectedFile.created_at)}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────
const PaymentValidationPage = () => {
  const { agencyCurrency, user, agencyCountry } = useContext(AuthContext);
  const { properties } = useContext(PropertyContext);
  const currencySymbol = getCurrencySymbol(agencyCurrency);

  const formatDateCtx = useCallback((dateString) => {
    return formatDate(dateString, { userPreference: user?.dateFormatPreference, agencyCountry });
  }, [user, agencyCountry]);

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [filterPropertyId, setFilterPropertyId] = useState('');
  const [searchText, setSearchText] = useState('');

  const fetchPayments = useCallback(async (propertyId = '', cursor = null, replace = true) => {
    const token = sessionStorage.getItem('access_token');
    const params = new URLSearchParams({ limit: '50' });
    if (propertyId) params.set('property_id', propertyId);
    if (cursor) params.set('cursor', cursor);

    const res = await fetch(buildApiUrl(`/payments/unverified?${params.toString()}`), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch unverified payments');
    const data = await res.json();
    const items = Array.isArray(data.data) ? data.data : [];
    setPayments(prev => replace ? items : [...prev, ...items]);
    setNextCursor(data.next_cursor || null);
    setHasMore(data.has_more || false);
    return items;
  }, []); // no changing deps — auto-select is handled by the caller

  // Initial load & reload when property filter changes
  useEffect(() => {
    setLoading(true);
    setSelectedPaymentId(null);
    fetchPayments(filterPropertyId, null, true)
      .then(items => {
        if (items.length > 0) setSelectedPaymentId(items[0].id);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [filterPropertyId, fetchPayments]);

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    await fetchPayments(filterPropertyId, nextCursor, false);
    setLoadingMore(false);
  };

  // When a payment is verified, remove it from the list
  const handleVerified = useCallback((paymentId) => {
    setPayments(prev => prev.filter(p => p.id !== paymentId));
    setSelectedPaymentId(prev => (prev === paymentId ? null : prev));
  }, []);

  // Filter list by search text (client-side)
  const filteredPayments = payments.filter(p => {
    if (!searchText) return true;
    const lower = searchText.toLowerCase();
    return (
      (p.property_name || '').toLowerCase().includes(lower) ||
      (p.room_name || '').toLowerCase().includes(lower) ||
      (p.reference || '').toLowerCase().includes(lower) ||
      (p.amount?.formatted || '').toLowerCase().includes(lower)
    );
  });

  return (
    <PageWrapper noPadding fullWidth>
      <div className="flex flex-col h-screen bg-gray-50">
        {/* ── Page Header ── */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <CheckCircleIcon className="h-6 w-6 text-emerald-600" />
                Payment Validation
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Review and verify unverified payments across all managed properties
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-full font-medium">
                {payments.length} pending{hasMore ? '+' : ''}
              </span>
              <button
                onClick={() => {
                  setLoading(true);
                  fetchPayments(filterPropertyId, null, true).finally(() => setLoading(false));
                }}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* ── Filters Bar ── */}
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
          <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row gap-3">
            {/* Property filter */}
            <div className="flex items-center gap-2 min-w-0 flex-1 sm:max-w-xs">
              <FunnelIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <select
                value={filterPropertyId}
                onChange={e => setFilterPropertyId(e.target.value)}
                className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
              >
                <option value="">All Properties</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div className="relative flex-1 sm:max-w-xs">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search reference, property…"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>
        </div>

        {/* ── Main Content (split panel) ── */}
        <div className="flex flex-1 min-h-0 max-w-screen-xl mx-auto w-full">
          {/* ── LEFT: Payment List ── */}
          <div className="w-full sm:w-80 lg:w-96 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center flex-1 py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
              </div>
            ) : filteredPayments.length === 0 ? (
              <div className="flex flex-col items-center justify-center flex-1 text-gray-400 py-16 px-4 text-center">
                <CheckCircleSolid className="h-14 w-14 mb-3 text-emerald-200" />
                <p className="font-medium text-gray-600">No unverified payments</p>
                <p className="text-sm mt-1">
                  {filterPropertyId ? 'Try clearing the property filter.' : 'All payments are verified.'}
                </p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                {filteredPayments.map(p => {
                  const isSelected = p.id === selectedPaymentId;
                  const amountStr = p.amount?.formatted ||
                    (p.amount?.amount != null ? `${currencySymbol}${(p.amount.amount / 100).toFixed(2)}` : '-');
                  return (
                    <button
                      key={p.id}
                      onClick={() => setSelectedPaymentId(p.id)}
                      className={`w-full text-left px-4 py-3 transition-colors flex flex-col gap-1 ${
                        isSelected
                          ? 'bg-emerald-50 border-l-4 border-emerald-500'
                          : 'hover:bg-gray-50 border-l-4 border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-sm text-gray-900 truncate flex items-center gap-1">
                          <BuildingOfficeIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          {p.property_name || 'Unknown Property'}
                        </span>
                        <span className={`font-bold text-sm flex-shrink-0 ${isSelected ? 'text-emerald-700' : 'text-gray-800'}`}>
                          {amountStr}
                        </span>
                      </div>
                      {p.room_name && (
                        <p className="text-xs text-gray-500 ml-5">Room: {p.room_name}</p>
                      )}
                      <div className="flex items-center justify-between gap-2 ml-5">
                        <span className="text-xs text-gray-400">{formatDateCtx(p.payment_date)}</span>
                        {p.reference && (
                          <span className="text-xs text-gray-400 font-mono truncate max-w-[100px]">{p.reference}</span>
                        )}
                      </div>
                    </button>
                  );
                })}

                {/* Load more */}
                {hasMore && (
                  <div className="p-3 text-center">
                    <button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="text-sm text-emerald-600 hover:text-emerald-700 font-medium disabled:opacity-50 flex items-center gap-1 mx-auto"
                    >
                      {loadingMore ? (
                        <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                      ) : null}
                      {loadingMore ? 'Loading...' : 'Load more'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── RIGHT: Payment Detail ── */}
          <div className="flex-1 min-w-0 overflow-hidden bg-white">
            <PaymentDetailPanel
              paymentId={selectedPaymentId}
              onVerified={handleVerified}
              agencyCurrency={agencyCurrency}
              user={user}
              agencyCountry={agencyCountry}
            />
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default PaymentValidationPage;

